"use client";

import {
	Button,
	Heading,
	Modal,
	Text,
	Image,
	Dots
} from "@pamperedchef/design-system";
import React from "react";
import { useRouter } from "next/navigation";

import * as styles from "./MyShows.module.scss";

export interface Show {
	id: string;
	name: string;
	consultantNumber: string;
	location: string;
	date: string;
}

export interface MyShowsProps {
	isOpen?: boolean;
	onClose: () => void;
	shows?: Show[];
	onConnect?: (showId: string) => void;
	title?: string;
	emptyState?: {
		title: string;
		description: string;
	};
	isLoading?: boolean;
}

/**
 * MyShows component
 * Renders a modal showing a user's active shows or an empty state.
 */
export function MyShows({
	isOpen = false,
	onClose,
	shows = [],
	onConnect,
	title = "My Shows",
	emptyState = {
		title: "No Shows Added Yet",
		description: "There are no active shows in your show list."
	},
	isLoading = false
}: MyShowsProps): JSX.Element {
	const router = useRouter();

	const handleConnect = (showPk: string, consultantNumber: string) => {
		onConnect?.(showPk);
		router.push(
			`/pws/consultant${consultantNumber}/host-landing/${showPk}`
		);
		onClose();
	};

	const isEmpty = shows.length === 0;

	return (
		<Modal
			className={styles["modal"]}
			isOpen={isOpen}
			aria-label={title}
			onClose={onClose}
			onClosePress={onClose}
		>
			<div className={styles["header"]}>
				<Heading tag="h2" className={styles["title"]}>
					{title}
				</Heading>
			</div>

			<div className={styles["content"]}>
				{isLoading ? (
					<div
						className={styles.loading}
						aria-live="polite"
						aria-label="Loading"
					>
						<Dots amount={3} />
					</div>
				) : isEmpty ? (
					<div className={styles["no-show-state"]}>
						<div className={styles["no-show-wrapper"]}>
							<div
								className={styles["no-show-image"]}
								role="img"
								aria-label="No Shows"
							/>
						</div>
						<Heading tag="h3" className={styles["no-show-title"]}>
							{emptyState.title}
						</Heading>
						<Text
							styledAs="mdr"
							className={styles["no-show-description"]}
						>
							{emptyState.description}
						</Text>
					</div>
				) : (
					<div className={styles["shows-list"]}>
						{shows.map((show) => (
							<div key={show.id} className={styles["show-item"]}>
								<div className={styles["show-info"]}>
									<Text
										tag="h4"
										styledAs="h6"
										className={styles["show-name"]}
									>
										{show.name}
									</Text>
									<Text
										styledAs="xsr"
										className={styles["show-details"]}
									>
										{show.location} | {show.date}
									</Text>
								</div>
								<Button
									kind="secondary"
									size="sm"
									onPress={() =>
										handleConnect(
											show.id,
											show.consultantNumber
										)
									}
									className={styles["connect-button"]}
								>
									Connect
								</Button>
							</div>
						))}
					</div>
				)}
			</div>
		</Modal>
	);
}
